export const SDK_VERSION = '8.6.1';

const SENSITIVE_QUERY_KEYS = new Set([
    'access_token',
    'api_key',
    'apikey',
    'auth',
    'authorization',
    'code',
    'email',
    'id_token',
    'jwt',
    'password',
    'passwd',
    'secret',
    'session',
    'session_id',
    'token',
]);

export function createId(prefix, cryptoObject = globalThis.crypto) {
    let value;

    if (typeof cryptoObject?.randomUUID === 'function') {
        value = cryptoObject.randomUUID().replaceAll('-', '');
    } else {
        const bytes = new Uint8Array(16);
        cryptoObject?.getRandomValues?.(bytes);
        value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    if (! value || /^0+$/.test(value)) {
        value = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(16, '0');
    }

    return `${prefix}_${value}`;
}

export function sanitizeUrl(value, baseUrl) {
    if (! value) {
        return null;
    }

    try {
        const url = new URL(value, baseUrl);

        if (! ['http:', 'https:'].includes(url.protocol)) {
            return null;
        }

        url.username = '';
        url.password = '';
        url.hash = '';

        for (const key of [...url.searchParams.keys()]) {
            if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
                url.searchParams.set(key, '[redacted]');
            }
        }

        // Browsers often leave "|" unescaped in Resource Timing names (e.g. Google Fonts).
        // Laravel's url:http,https rule rejects those raw characters.
        return url.toString().replaceAll('|', '%7C');
    } catch {
        return null;
    }
}

export function detectClientContext(browser) {
    const navigator = browser.navigator ?? {};
    const userAgent = String(navigator.userAgent ?? '').slice(0, 1024);
    const browserIdentity = detectBrowser(userAgent, navigator.userAgentData?.brands);
    const operatingSystem = detectOperatingSystem(userAgent, navigator.userAgentData?.platform);
    const connection = navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection;
    let timezone = null;

    try {
        timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
        timezone = null;
    }

    return {
        device_type: detectDeviceType(userAgent, navigator.userAgentData?.mobile, navigator.maxTouchPoints),
        browser_name: browserIdentity.name,
        browser_version: browserIdentity.version,
        os_name: operatingSystem.name,
        os_version: operatingSystem.version,
        user_agent: userAgent || null,
        language: typeof navigator.language === 'string' ? navigator.language.slice(0, 35) : null,
        languages: Array.isArray(navigator.languages)
            ? navigator.languages.filter((language) => typeof language === 'string').slice(0, 10).map((language) => language.slice(0, 35))
            : null,
        timezone,
        device_pixel_ratio: finiteNumber(browser.devicePixelRatio, 0, 100),
        touch_capable: Number(navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in browser,
        connection_type: typeof connection?.type === 'string' ? connection.type.slice(0, 30) : null,
        effective_connection_type: typeof connection?.effectiveType === 'string' ? connection.effectiveType.slice(0, 20) : null,
        downlink_mbps: finiteNumber(connection?.downlink, 0, 10000),
        round_trip_time_ms: finiteNumber(connection?.rtt, 0, 600000, true),
    };
}

const PaidClickSources = {
    gclid: 'google',
    gbraid: 'google',
    wbraid: 'google',
    gclsrc: 'google',
    dclid: 'google',
    msclkid: 'bing',
    ttclid: 'tiktok',
    twclid: 'twitter',
    li_fat_id: 'linkedin',
    epik: 'pinterest',
};

export function attributionFromUrl(value, baseUrl) {
    try {
        const url = new URL(value, baseUrl);
        const read = (name) => {
            const parameter = url.searchParams.get(name);

            return parameter ? parameter.slice(0, 255) : null;
        };
        const paidSource = Object.entries(PaidClickSources).find(([parameter]) => read(parameter));

        return {
            utm_source: read('utm_source') || paidSource?.[1] || null,
            utm_medium: read('utm_medium') || (paidSource ? 'cpc' : null),
            utm_campaign: read('utm_campaign'),
            utm_term: read('utm_term'),
            utm_content: read('utm_content'),
        };
    } catch {
        return {
            utm_source: null,
            utm_medium: null,
            utm_campaign: null,
            utm_term: null,
            utm_content: null,
        };
    }
}

function detectBrowser(userAgent, brands) {
    const brand = Array.isArray(brands)
        ? brands.find((item) => /Microsoft Edge|Google Chrome|Opera|Chromium/i.test(item?.brand ?? ''))
        : null;
    const candidates = [
        ['Edge', /EdgA?\/([\d.]+)/],
        ['Opera', /(?:OPR|Opera)\/([\d.]+)/],
        ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
        ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
        ['Safari', /Version\/([\d.]+).*Safari/],
    ];

    for (const [name, pattern] of candidates) {
        const match = userAgent.match(pattern);

        if (match) {
            return { name, version: match[1] };
        }
    }

    if (brand) {
        return {
            name: brand.brand.replace('Microsoft Edge', 'Edge').replace('Google Chrome', 'Chrome'),
            version: String(brand.version ?? '').slice(0, 50) || null,
        };
    }

    return { name: 'Unknown', version: null };
}

function detectOperatingSystem(userAgent, platform) {
    const candidates = [
        ['Windows', /Windows NT ([\d.]+)/],
        ['iOS', /(?:iPhone|iPad|iPod).*OS ([\d_]+)/],
        ['Android', /Android ([\d.]+)/],
        ['Chrome OS', /CrOS [^ ]+ ([\d.]+)/],
        ['macOS', /Mac OS X ([\d_]+)/],
        ['Linux', /Linux/],
    ];

    for (const [name, pattern] of candidates) {
        const match = userAgent.match(pattern);

        if (match) {
            return { name, version: match[1]?.replaceAll('_', '.') ?? null };
        }
    }

    return { name: typeof platform === 'string' && platform ? platform.slice(0, 50) : 'Unknown', version: null };
}

function detectDeviceType(userAgent, mobileHint, maxTouchPoints) {
    if (/iPad|Tablet|PlayBook|Silk/i.test(userAgent) || (/Android/i.test(userAgent) && ! /Mobile/i.test(userAgent))) {
        return 'tablet';
    }

    if (mobileHint === true || /Mobi|iPhone|iPod|Android/i.test(userAgent)) {
        return 'mobile';
    }

    if (/Macintosh/i.test(userAgent) && Number(maxTouchPoints || 0) > 1) {
        return 'tablet';
    }

    return userAgent ? 'desktop' : 'unknown';
}

function finiteNumber(value, minimum, maximum, integer = false) {
    const number = Number(value);

    if (! Number.isFinite(number)) {
        return null;
    }

    const bounded = Math.max(minimum, Math.min(maximum, number));

    return integer ? Math.round(bounded) : bounded;
}

export class BrowserValueStore {
    constructor(browser, storeId) {
        this.browser = browser;
        this.prefix = `ailog:${storeId}:`;
    }

    storageKey(name) {
        return this.prefix + name;
    }

    get(key) {
        const storageKey = this.storageKey(key);

        try {
            const value = this.browser.localStorage?.getItem(storageKey);

            if (value !== null && value !== undefined) {
                return value;
            }
        } catch {
            // Storage denial falls through to a first-party cookie.
        }

        return this.readCookie(storageKey);
    }

    set(key, value, maxAge = 31536000) {
        const storageKey = this.storageKey(key);

        try {
            this.browser.localStorage?.setItem(storageKey, value);
        } catch {
            // Cookie fallback below keeps identity across page loads when possible.
        }

        try {
            const secure = this.browser.location?.protocol === 'https:' ? '; Secure' : '';
            this.browser.document.cookie = `${this.cookieName(storageKey)}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
        } catch {
            // Ephemeral identity is the privacy-safe fallback when storage is unavailable.
        }
    }

    readCookie(storageKey) {
        try {
            const name = `${this.cookieName(storageKey)}=`;
            const cookie = this.browser.document.cookie
                .split(';')
                .map((part) => part.trim())
                .find((part) => part.startsWith(name));

            return cookie ? decodeURIComponent(cookie.slice(name.length)) : null;
        } catch {
            return null;
        }
    }

    cookieName(storageKey) {
        return storageKey.replaceAll(/[^A-Za-z0-9_-]/g, '_');
    }
}

export class IdentityManager {
    constructor(valueStore, timeoutMinutes = 30, now = () => Date.now(), cryptoObject = globalThis.crypto) {
        this.valueStore = valueStore;
        this.timeoutMilliseconds = Math.max(5, timeoutMinutes) * 60000;
        this.now = now;
        this.cryptoObject = cryptoObject;
    }

    visitorId() {
        const existing = this.valueStore.get('visitor');

        if (/^v_[A-Za-z0-9_-]{16,64}$/.test(existing ?? '')) {
            return existing;
        }

        const visitorId = createId('v', this.cryptoObject);
        this.valueStore.set('visitor', visitorId);

        return visitorId;
    }

    session() {
        return this.syncSession(null);
    }

    touch(session) {
        const synced = this.syncSession(session);

        if (session !== null) {
            session.id = synced.id;
            session.lastActivity = synced.lastActivity;
        }

        return synced;
    }

    syncSession(inMemorySession) {
        const currentTime = this.now();
        const stored = this.parseSession(this.valueStore.get('session'));

        if (stored !== null && currentTime - stored.lastActivity <= this.timeoutMilliseconds) {
            stored.lastActivity = currentTime;
            this.persistSession(stored);

            return stored;
        }

        if (
            inMemorySession !== null
            && currentTime - inMemorySession.lastActivity <= this.timeoutMilliseconds
        ) {
            inMemorySession.lastActivity = currentTime;
            this.persistSession(inMemorySession);

            return inMemorySession;
        }

        const created = { id: createId('s', this.cryptoObject), lastActivity: currentTime };
        this.persistSession(created);

        const raced = this.parseSession(this.valueStore.get('session'));

        if (
            raced !== null
            && raced.id !== created.id
            && currentTime - raced.lastActivity <= this.timeoutMilliseconds
        ) {
            raced.lastActivity = currentTime;
            this.persistSession(raced);

            return raced;
        }

        return created;
    }

    parseSession(value) {
        try {
            const parsed = JSON.parse(value);

            return /^s_[A-Za-z0-9_-]{16,64}$/.test(parsed?.id) && Number.isFinite(parsed?.lastActivity)
                ? parsed
                : null;
        } catch {
            return null;
        }
    }

    persistSession(session) {
        this.valueStore.set('session', JSON.stringify(session), 2592000);
    }
}

export class ActiveClock {
    constructor(startedAt = Date.now(), visible = true, idleThresholdMs = 5000) {
        this.startedAt = startedAt;
        this.activeStartedAt = visible ? startedAt : null;
        this.hiddenStartedAt = visible ? null : startedAt;
        this.accumulatedActiveMilliseconds = 0;
        this.accumulatedHiddenMilliseconds = 0;
        this.accumulatedIdleMilliseconds = 0;
        this.lastInteractionAt = startedAt;
        this.idleStartedAt = null;
        this.idleThresholdMs = Math.max(1000, idleThresholdMs);
    }

    markInteraction(now = Date.now()) {
        this.closeIdlePeriod(now);
        this.lastInteractionAt = now;
    }

    setVisible(visible, now = Date.now()) {
        this.closeIdlePeriod(now);

        if (visible && this.hiddenStartedAt !== null) {
            this.accumulatedHiddenMilliseconds += Math.max(0, now - this.hiddenStartedAt);
            this.hiddenStartedAt = null;
        }

        if (visible && this.activeStartedAt === null) {
            this.activeStartedAt = now;
            this.lastInteractionAt = now;
        }

        if (! visible && this.activeStartedAt !== null) {
            this.accumulatedActiveMilliseconds += Math.max(0, now - this.activeStartedAt);
            this.activeStartedAt = null;
            this.idleStartedAt = null;
        }

        if (! visible && this.hiddenStartedAt === null) {
            this.hiddenStartedAt = now;
        }
    }

    closeIdlePeriod(now = Date.now()) {
        if (this.idleStartedAt !== null) {
            this.accumulatedIdleMilliseconds += Math.max(0, now - this.idleStartedAt);
            this.idleStartedAt = null;
        }
    }

    snapshot(now = Date.now()) {
        let activeMilliseconds = this.accumulatedActiveMilliseconds;
        let hiddenMilliseconds = this.accumulatedHiddenMilliseconds;
        let idleMilliseconds = this.accumulatedIdleMilliseconds;

        if (this.activeStartedAt !== null) {
            activeMilliseconds += Math.max(0, now - this.activeStartedAt);

            if (now - this.lastInteractionAt >= this.idleThresholdMs) {
                const idleStart = this.idleStartedAt ?? (this.lastInteractionAt + this.idleThresholdMs);
                idleMilliseconds += Math.max(0, now - idleStart);
            }
        }

        if (this.hiddenStartedAt !== null) {
            hiddenMilliseconds += Math.max(0, now - this.hiddenStartedAt);
        }

        return {
            durationSeconds: Math.max(0, Math.floor((now - this.startedAt) / 1000)),
            activeDurationSeconds: Math.max(0, Math.floor(activeMilliseconds / 1000)),
            hiddenDurationSeconds: Math.max(0, Math.floor(hiddenMilliseconds / 1000)),
            idleDurationSeconds: Math.max(0, Math.floor(idleMilliseconds / 1000)),
        };
    }
}

export class EventBuffer {
    constructor(browser, storeId, maximumEvents = 100) {
        this.browser = browser;
        this.storageKey = `ailog:${storeId}:pending`;
        this.maximumEvents = maximumEvents;
        this.memoryEvents = this.coalesce(this.read());
        this.persist();
    }

    add(visitorId, sessionId, event) {
        if (this.isReplaceable(event)) {
            const pageViewId = event?.data?.page_view_id ?? null;

            this.memoryEvents = this.memoryEvents.filter((item) => {
                if (item.visitorId !== visitorId || item.sessionId !== sessionId) {
                    return true;
                }

                if (! this.isReplaceable(item.event) || item.event.type !== event.type) {
                    return true;
                }

                if (event.type === 'custom') {
                    return item.event.id !== event.id;
                }

                return (item.event?.data?.page_view_id ?? null) !== pageViewId;
            });
        }

        this.memoryEvents.push({ visitorId, sessionId, event });
        this.trimPreferringPageViews();
        this.persist();
    }

    take(maximum = 10, visitorId = null, sessionId = null) {
        if (visitorId !== null && sessionId !== null) {
            this.dropLeadingMismatched(visitorId, sessionId);
        }

        const first = this.memoryEvents[0];

        if (! first) {
            return [];
        }

        const selected = [];

        for (const item of this.memoryEvents) {
            if (selected.length >= maximum || item.visitorId !== first.visitorId || item.sessionId !== first.sessionId) {
                break;
            }

            selected.push(item);
        }

        this.memoryEvents.splice(0, selected.length);
        this.persist();

        return selected;
    }

    dropLeadingMismatched(visitorId, sessionId) {
        let dropped = false;

        while (
            this.memoryEvents[0]
            && (this.memoryEvents[0].visitorId !== visitorId || this.memoryEvents[0].sessionId !== sessionId)
        ) {
            this.memoryEvents.shift();
            dropped = true;
        }

        if (dropped) {
            this.persist();
        }
    }

    dropMismatched(visitorId, sessionId) {
        const filtered = this.memoryEvents.filter(
            (item) => item.visitorId === visitorId && item.sessionId === sessionId,
        );

        if (filtered.length === this.memoryEvents.length) {
            return;
        }

        this.memoryEvents = filtered;
        this.persist();
    }

    restore(items) {
        this.memoryEvents = this.coalesce([...items, ...this.memoryEvents]);
        this.trimPreferringPageViews();
        this.persist();
    }

    alignSession(visitorId, sessionId) {
        let changed = false;

        this.memoryEvents = this.memoryEvents.map((item) => {
            if (item.visitorId !== visitorId || item.sessionId === sessionId) {
                return item;
            }

            changed = true;

            return { ...item, sessionId };
        });

        if (changed) {
            this.persist();
        }
    }

    count() {
        return this.memoryEvents.length;
    }

    clear() {
        this.memoryEvents = [];
        this.persist();
    }

    coalesce(events) {
        const result = [];
        const latestReplaceable = new Map();

        for (const item of events) {
            const type = item?.event?.type;

            if (! this.isReplaceable(item?.event)) {
                result.push(item);
                continue;
            }

            const key = [
                item.visitorId,
                item.sessionId,
                type,
                type === 'custom' ? item.event?.id ?? '' : item.event?.data?.page_view_id ?? '',
            ].join(':');

            if (latestReplaceable.has(key)) {
                result[latestReplaceable.get(key)] = item;
            } else {
                latestReplaceable.set(key, result.length);
                result.push(item);
            }
        }

        return result;
    }

    isReplaceable(event) {
        return event?.type === 'page_update'
            || event?.type === 'page_resources'
            || (event?.type === 'custom' && event?.data?.category === 'form' && event?.data?.name === 'form_activity');
    }

    trimPreferringPageViews() {
        while (this.memoryEvents.length > this.maximumEvents) {
            let dropIndex = -1;

            for (let index = this.memoryEvents.length - 1; index >= 0; index -= 1) {
                if (this.memoryEvents[index].event?.type !== 'page_view') {
                    dropIndex = index;
                    break;
                }
            }

            if (dropIndex === -1) {
                this.memoryEvents.shift();
            } else {
                this.memoryEvents.splice(dropIndex, 1);
            }
        }
    }

    read() {
        try {
            const events = JSON.parse(this.browser.localStorage?.getItem(this.storageKey) ?? '[]');

            return Array.isArray(events) ? events.slice(-this.maximumEvents) : [];
        } catch {
            return [];
        }
    }

    persist() {
        try {
            this.browser.localStorage?.setItem(this.storageKey, JSON.stringify(this.memoryEvents));
        } catch {
            // The in-memory queue remains available for this page lifecycle.
        }
    }
}

export function trackingIsAllowed(browser, config) {
    if (! config.storeId || config.disabled) {
        return false;
    }

    if (config.respectDoNotTrack && (browser.navigator?.doNotTrack === '1' || browser.navigator?.globalPrivacyControl === true)) {
        return false;
    }

    if (config.consentMode !== 'required') {
        return true;
    }

    try {
        return browser.AILogConsent?.granted === true || browser.localStorage?.getItem('ailog:consent') === 'granted';
    } catch {
        return browser.AILogConsent?.granted === true;
    }
}
